// src/data/editions/corporate.js
//
// CORPORATE (GENERIC) edition — the standard skin for any corporate org that
// hasn't been given a specialized vertical (insurance, aviation, ...). Neutral
// employer story: capacity -> strain -> absence & leave. No claims-P&L framing,
// no operator jargon. DRAFT copy — reviewed via docs/plans/EDITIONS-REVIEW.md.

const corporate = {
    id: 'corporate',
    label: 'Corporate — Generic',

    palette: { deep: '#02486e', blue: '#0679af', yellow: '#f6c453' },

    pageTitles: {
        'cc-overview': 'Workforce Readiness Overview',
        'cc-triangulation': 'Triangulation: Members, the Organization, the Average',
        'cc-data': 'Data Intake: Your Organization’s Own Figures',
        'cc-ai': 'AI Intelligence & Charting',
        'cc-readiness': 'Readiness & Strain',
        'cc-predictive': 'Absence & Leave Forecast',
        'cc-roi': 'Program ROI & Impact',
        'cc-feedback': 'Anonymous Pulse & Sentiment',
        default: 'Workforce Command Center'
    },

    trendSignalLabel: 'Absence signal',

    summary: {
        source: 'demo', // drives the console's "Modeled example" badge (T0.3)
        capacityScore: 82,
        capacityTrend: 3,
        workforce: 1200,
        highSignalCohort: 142,
        topPriorityGroup: 'Operations',
        topPulseTheme: 'Workload',
        participationRate: 64,
        modeledClaimsAvoidance: '$1.4M',
        modeledStdClaims: { low: 20, high: 32, windowDays: 90 },
        modeledLtdTransition: { low: 6, high: 10 },
        leaveProbability: { mid: 18, low: 14, high: 22 },
        // Overview roll-up card copy (2nd + 3rd cards).
        outlookTitle: 'Leave Outlook',
        outlookNoun: 'extended leaves',
        outlookUnit: 'leaves',
        financialNoun: 'absence-cost avoidance'
    },

    capacityTrend: [
        { date: '1st', capacity: 79, signal: 22 },
        { date: '4th', capacity: 80, signal: 21 },
        { date: '8th', capacity: 78, signal: 24 },
        { date: '12th', capacity: 81, signal: 20 },
        { date: '16th', capacity: 80, signal: 21 },
        { date: '20th', capacity: 82, signal: 18 },
        { date: '24th', capacity: 81, signal: 19 },
        { date: '28th', capacity: 82, signal: 18 },
        { date: 'Today', capacity: 82, signal: 18 }
    ],

    readiness: {
        scoreLabel: 'Workforce Capacity Score',
        signalSubtext: 'workload + scheduled meeting density',
        groups: [
            { name: 'Operations', pressure: 79, note: 'Sustained delivery load', responses: 96 },
            { name: 'Sales', pressure: 74, note: 'Quarter-end push', responses: 41 },
            { name: 'Engineering', pressure: 72, note: 'Steady throughput, rising hours', responses: 34 },
            { name: 'Customer Support', pressure: 69, note: 'Elevated contact volume', responses: 22 },
            { name: 'People & Culture', pressure: 63, note: 'Within normal range', responses: 4 } // below floor
        ],
        signals: [
            { label: 'Self-reported strain trend', value: '+5%', tier: 'Intermediate', tone: 'amber' },
            { label: 'Sick leave & absenteeism', value: '+7%', tier: 'Lagging', tone: 'amber' },
            { label: 'Missed deadlines / delayed work', value: '+4%', tier: 'Lagging', tone: 'amber' },
            { label: 'Manager check-in signal', value: 'Steady', tier: 'Intermediate', tone: 'emerald' }
        ]
    },

    feedback: {
        scopes: [
            { id: 'org', label: 'Organization-wide', responses: 312 },
            { id: 'operations', label: 'Operations', responses: 96 },
            { id: 'sales', label: 'Sales', responses: 41 },
            { id: 'engineering', label: 'Engineering', responses: 34 },
            { id: 'people', label: 'People & Culture', responses: 4 } // below floor -> suppressed
        ],
        submissions: [
            { id: 1, time: '2 hours ago', theme: 'Workload', sentiment: 'risk', scope: 'operations',
              text: 'The volume this month is relentless. We spend more time working around the tooling than doing the actual work.' },
            { id: 2, time: '5 hours ago', theme: 'Tools', sentiment: 'positive', scope: 'engineering',
              text: 'The new pipeline is saving hours of manual effort every week. Good investment by leadership.' },
            { id: 3, time: 'Yesterday', theme: 'Culture', sentiment: 'positive', scope: 'operations',
              text: 'Communication around the reorg has been much clearer this month. The transparency is appreciated.' },
            { id: 4, time: 'Yesterday', theme: 'Workload', sentiment: 'risk', scope: 'sales',
              text: 'Quarter-end targets on top of coverage gaps — the team is underwater and it shows.' },
            { id: 5, time: '2 days ago', theme: 'Recognition', sentiment: 'positive', scope: 'operations',
              text: 'Felt genuinely supported when my manager flagged my workload before I had to ask.' }
        ],
        sentiment: {
            positiveSummary: 'Strong response to recent transparency in leadership communication and tooling investment.',
            emergingTheme: 'Workload volume and process friction are the most frequent sources of frustration.'
        }
    },

    predictive: {
        title: 'The Absence & Leave Funnel',
        intro: 'In the modeled baseline, a share of the high-strain cohort would be expected to convert sustained strain into unplanned absence, and a smaller share into extended leave. This is a directional projection from leading signals - not a forecast - and it needs a 6-12 month window and a comparison baseline before any causal claim.',
        lever: 'Earlier support routing',
        ownLabel: 'Unplanned-absence rate',
        tiers: [
            { value: '1,200', label: 'Total workforce', source: 'observed', tone: 'neutral', width: 100 },
            { value: '142', label: 'High-strain cohort', source: 'observed', tone: 'brand', width: 88 },
            { value: '55-80', label: 'Modeled unplanned absence days - qtr', source: 'modeled', tone: 'amber', width: 72 },
            { value: '20-32', label: 'Modeled extended leaves', source: 'modeled', tone: 'amber', width: 56 },
            { value: '6-10', label: 'Long-term leave transitions - tail', source: 'modeled', tone: 'muted', width: 44 }
        ],
        cards: [
            { title: 'Strain-Absence Association', value: '~4.2x', valueTone: 'amber', level: 'Intermediate', source: 'modeled', dark: false,
              subtext: 'Modeled association - not causal',
              body: 'Groups sustaining low capacity scores show a higher rate of stress-related absence. In comparable modeled populations the association is roughly 4.2x - directional, and confirmed only against an employer baseline.' },
            { title: 'Modeled Leave Probability', value: '18%', valueTone: 'white', level: 'Lagging', source: 'modeled', dark: true,
              subtext: 'Midpoint - 14-22% band',
              body: 'Modeled probability that unmanaged strain in a high-load group converts to an extended leave this quarter. The band widens or narrows as employer baseline data is added.' },
            { title: 'Proactive Utilization Lift', value: '+34%', valueTone: 'emerald', level: 'Leading', source: 'observed', dark: false,
              subtext: 'Observed in engaged cohorts - attribution pending',
              body: 'Cohorts engaged with AI Coach recommendations show higher proactive routing into existing EAP / paramedical benefits - a leading indicator, shown as an association rather than a causal result.' }
        ],
        footer: {
            lead: 'These are models, not forecasts.',
            note: 'Projections are conservatively attributed and reported at the group level only. Absence, leave and long-term transitions are lagging outcomes that require a longer window and a comparison baseline before any causal claim. EAS reports what it measures and labels what it models.'
        }
    },

    roi: {
        currency: 'CAD',
        locale: 'en-CA',
        benchSource: 'sector reference',
        replacementMultiple: 0.5,
        turnoverRate: 12,
        termLabels: {
            lostTime: 'Avoided Lost Time',
            lostTimeBasis: 'Baseline absence days x loaded daily cost x modeled reduction.'
        }
    },

    copilot: { exampleScope: 'Operations' }
};

export default corporate;
